// ABOUTME: Application configuration from environment variables.
// ABOUTME: Provides typed access to build settings, API URLs, and feature flags.

/* eslint-disable no-restricted-syntax -- the one module allowed to read import.meta.env; everything else reads config */

import { STORAGE_KEYS } from "@shared/constants/storageKeys"
import { localizeDomain, resolveAppServiceUrl } from "@/model/domain"

const buildAuthUrl = (baseUrl: string, useCurrentLocation = false): string => {
  const params = new URLSearchParams()

  const lang = localStorage.getItem(STORAGE_KEYS.LANGUAGE)
  if (lang) {
    params.append("lang", lang)
  }

  const returnUrl = useCurrentLocation ? window.location.href : window.location.origin
  params.append("return_to", returnUrl)

  const queryString = params.toString()
  return queryString ? `${baseUrl}?${queryString}` : baseUrl
}

// Auth base localized once — the served host is fixed for the session.
const AUTH_URL = localizeDomain((import.meta.env.VITE_AUTH_URL as string | undefined) ?? "")

const APP_URL_CANONICAL = (import.meta.env.VITE_APP_URL as string | undefined) ?? ""

const config = {
  BUILD_VERSION: (import.meta.env.VITE_BUILD_VERSION as string | undefined) ?? "development",
  LOG_LEVEL: (import.meta.env.VITE_LOG_LEVEL as string | undefined) ?? "info",
  CHECK_UPDATE_TIME_IN_MIN: Number(import.meta.env.VITE_CHECK_UPDATE_TIME_IN_MIN) || 10,

  // Every domain-bearing URL lives here and resolves once at init — the served
  // host is fixed per session — so feature code never reads a raw
  // *.trainingendurance.com URL directly. Two groups (see model/domain):
  //  - services under the app host in the single-origin model (GraphQL, Kratos,
  //    download constructor) go through resolveAppServiceUrl and follow the
  //    CURRENT origin, so the manual eu./ru. entry points and the .ru domain
  //    all route API traffic through the host the user is actually on;
  //  - services on their own hosts (avatar, chatwoot, store, mcp) only get the
  //    .com→.ru TLD rewrite via localizeDomain.
  API_URL_TRAINING: resolveAppServiceUrl(
    (import.meta.env.VITE_API_URL_TRAINING as string | undefined) ?? "",
    APP_URL_CANONICAL,
  ),
  APP_URL: localizeDomain(APP_URL_CANONICAL),
  // Ory Kratos API base. Local dev uses the same-origin "/kratos" Vite-proxy path;
  // deployed envs serve it under the app host (same-origin, cookies work natively).
  KRATOS_API_URL: resolveAppServiceUrl(
    (import.meta.env.VITE_KRATOS_API_URL as string | undefined) ?? "",
    APP_URL_CANONICAL,
  ),
  // Centrifugo is served under the app host too (ADR-0001 §3.5), so the realtime
  // connection follows the entry point the user chose, like the GraphQL calls.
  REALTIME_URL: resolveAppServiceUrl(`${APP_URL_CANONICAL}/realtime`, APP_URL_CANONICAL),
  AVATAR_URL: localizeDomain((import.meta.env.VITE_AVATAR_URL as string | undefined) ?? ""),
  DOWNLOAD_CONSTRUCTOR: resolveAppServiceUrl(
    (import.meta.env.VITE_DOWNLOAD_CONSTRUCTOR as string | undefined) ?? "",
    APP_URL_CANONICAL,
  ),
  CHATWOOT_BASE_URL: localizeDomain((import.meta.env.VITE_CHATWOOT_BASE_URL as string | undefined) ?? ""),
  CHATWOOT_WEBSITE_TOKEN: (import.meta.env.VITE_CHATWOOT_WEBSITE_TOKEN as string | undefined) ?? "",
  // The store's shop-api (Vendure). Local dev reads it through the same-origin "/store-api"
  // Vite proxy: the store's CORS does not let the localhost origin in.
  STORE_API_URL: localizeDomain(
    (import.meta.env.VITE_STORE_API_URL as string | undefined) ?? "https://store.trainingendurance.com/shop-api",
  ),
  // The storefront where an order is placed and paid for. It lives on the .ru site only — its
  // /en/ pages redirect to /ru/ — so it is the same address from either domain.
  SHOP_URL: "https://trainingendurance.ru/shop",
  // The same storefront through a host outside trainingendurance.com/.ru, which redirects to SHOP_URL keeping the
  // path and query (infrastructure netangels/store/ingress-production.yaml). A link the native apps must give to the
  // system browser goes through it: they open any trainingendurance host in their own WebView, with no way back
  // (iOS: KratosAuth.isTrainingEnduranceHost), and hand every other host to the browser.
  SHOP_HANDOFF_URL: "https://ru-shop.getsportup.com",
  MCP_URL: localizeDomain("https://mcp.trainingendurance.com"),
  SYSTEM_USER_ID: (import.meta.env.VITE_SYSTEM_USERID as string | undefined) ?? "",

  // Build mode and dev-only behaviour (Apollo devtools hook, Mixpanel debug, HMR stubs).
  MODE: import.meta.env.MODE,
  IS_DEV: Boolean(import.meta.env.DEV),

  // Third-party services; empty means "not configured", and the init functions return early.
  SENTRY_DSN: (import.meta.env.VITE_SENTRY_DSN as string | undefined) ?? "",
  MIXPANEL_TOKEN: (import.meta.env.VITE_MIXPANEL_TOKEN as string | undefined) ?? "",
  CLARITY_ID: (import.meta.env.VITE_CLARITY_ID as string | undefined) ?? "",
  GA_TRACKING_ID: (import.meta.env.VITE_GA_TRACKING_ID as string | undefined) ?? "",
  GA_ENABLED: import.meta.env.VITE_GA_ENABLED === "true",
  // The .ru version's own: Yandex Metrika instead of the services above, Metric (errors.trainingendurance.ru)
  // instead of Sentry — model/metrika.ts, model/sentry.ts.
  YANDEX_METRIKA_ID: (import.meta.env.VITE_YANDEX_METRIKA_ID as string | undefined) ?? "",
  METRIC_DSN: (import.meta.env.VITE_METRIC_DSN as string | undefined) ?? "",

  // Development switches: mock the native wrapper / the BLE lactometer, verbose BLE logging.
  USE_MOCK_NATIVE_BRIDGE: import.meta.env.VITE_USE_MOCK_NATIVE_BRIDGE === "true",
  USE_MOCK_LACTATE_DEVICE: import.meta.env.VITE_USE_MOCK_LACTATE_DEVICE === "true",
  DEBUG_BLUETOOTH: import.meta.env.VITE_DEBUG_BLUETOOTH === "true",

  // Read at call time, not at load: the in-app-auth flag is a runtime kill-switch and its
  // tests stub the env per case (model/auth/__tests__/inAppAuthFlag.test.ts).
  inAppAuthDefault: (): string => String(import.meta.env.VITE_INAPP_AUTH ?? ""),

  // Auth URL helper functions.
  linkToLogin: (): string => buildAuthUrl(AUTH_URL, false),
  linkToLogout: (): string => buildAuthUrl(`${AUTH_URL}/logout`, false),
  linkToSettings: (): string => buildAuthUrl(`${AUTH_URL}/settings`, true),
}

export default config
